import { Injectable } from '@angular/core';
import { Observable, throwError } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import {
  AssistantKey,
  TestCase,
  TestResult,
  listSuccessEvaluatorResults$,
  listSuccessEvaluatorStatsForAssistant$,
} from '@vendasta/protos/ai_assistants/v1alpha1';
import { isResourceExhausted } from '../../core/utils/resource-exhausted.util';

export interface JobStat {
  testCase: TestCase;
  // Fraction of evaluated results that passed, 0-1.
  successRate: number;
}

/** One page of a single evaluator's scored conversations. */
export interface EvaluatorResultsPage {
  results: TestResult[];
  // Pass back to fetch the following page; empty when there is none.
  nextCursor: string;
  hasMore: boolean;
}

/**
 * A few rows to open on, so the modal settles quickly — every result carries a
 * full transcript, so the first page is the one the user waits for.
 */
export const INITIAL_EVALUATOR_RESULTS_PAGE_SIZE = 3;

/** Pages after the first, once the user has asked for more. */
export const EVALUATOR_RESULTS_PAGE_SIZE = 25;

/**
 * Page size retried after a response exceeds the gRPC message limit. Matches the
 * size the test-results path settled on for the same payload type.
 */
const EVALUATOR_RESULTS_FALLBACK_PAGE_SIZE = 5;

@Injectable({
  providedIn: 'root',
})
export class JobsToBeDoneService {
  listJobsToBeDone(assistantKey: Partial<AssistantKey>): Observable<JobStat[]> {
    return listSuccessEvaluatorStatsForAssistant$({ assistant: assistantKey }).pipe(
      map((response) =>
        response.evaluators
          // A zero evaluated count means the job has no results yet; only surface ones with data.
          .filter((stat) => stat.evaluatedCount > 0 && stat.testCase !== undefined)
          .map((stat) => ({ testCase: stat.testCase as TestCase, successRate: stat.successRate })),
      ),
    );
  }

  /**
   * Fetches one page of the conversations a single evaluator scored, most
   * recently evaluated first. Only scored conversations are returned, so the page
   * covers the same set the evaluator's success rate is computed from. Pass the
   * previous page's nextCursor to continue.
   *
   * A page may come back smaller than asked for: an over-limit response is retried
   * at a smaller size rather than failed.
   */
  listEvaluatorResults(
    assistantKey: Partial<AssistantKey>,
    testCaseId: string,
    cursor = '',
  ): Observable<EvaluatorResultsPage> {
    const pageSize = cursor ? EVALUATOR_RESULTS_PAGE_SIZE : INITIAL_EVALUATOR_RESULTS_PAGE_SIZE;
    return this.requestPage(assistantKey, testCaseId, cursor, pageSize).pipe(
      // Response size follows conversation length, not row count, so a page can
      // exceed the gRPC message limit at any size. Retrying smaller keeps the page
      // usable; the first page is already below the fallback and is not retried.
      catchError((err) => {
        if (pageSize > EVALUATOR_RESULTS_FALLBACK_PAGE_SIZE && isResourceExhausted(err)) {
          return this.requestPage(assistantKey, testCaseId, cursor, EVALUATOR_RESULTS_FALLBACK_PAGE_SIZE);
        }
        return throwError(() => err);
      }),
    );
  }

  private requestPage(
    assistantKey: Partial<AssistantKey>,
    testCaseId: string,
    cursor: string,
    pageSize: number,
  ): Observable<EvaluatorResultsPage> {
    return listSuccessEvaluatorResults$({
      assistant: assistantKey,
      testCaseId,
      pagingOptions: { pageSize, cursor },
    }).pipe(
      map((response) => ({
        results: response.testResults ?? [],
        nextCursor: response.pagingMetadata?.nextCursor ?? '',
        hasMore: response.pagingMetadata?.hasMore ?? false,
      })),
    );
  }
}
